<script>
	// Stores
	import ACLStore from '@/Stores/ACLStore.js';
	// Components
	import Icon from '@/Components/Icon.svelte';
	// Partials
	import StatsReport from '@/Partials/User/_StatsReport.svelte';
	import { CurrencyFormat, NumberFormat } from '@/Services/Helpers';

	export let user;

	$: stats = (sortCol || sortDesc) && user && user.betting_stats && user.betting_stats.data && sortStats();
	$: daily = user && user.daily_betting_stats;
	$: user && user.betting_stats && user.betting_stats.data && calcBettingStatsByCategory();

	let showStatsReport = false;

	let racingClientResult = 0;
	let sportsClientResult = 0;
	let multiClientResult = 0;
	let racingTurnover = 0;
	let sportsTurnover = 0;
	let multiTurnover = 0;
	let racingCount = 0;
	let sportsCount = 0;
	let multiCount = 0;
	let racingTotalPercentage = 0;
	let sportsTotalPercentage = 0;
	let multiTotalPercentage = 0;

	const statCols = {
		Category: 'bet_category.name',
		Classification: 'classification.name',
		'Client Result': 'client_result',
		Turnover: 'turnover',
		'Bonus Turnover': 'bonus_turnover',
		Percentage: 'percentage',
		'Num. Bets': 'bet_count',
		'Num. Bonus Bets': 'bonus_bet_count',
		'Avg. Bet': 'bet_average'
	};
	let sortCol = 'client_result';
	let sortDesc = true;

	function sortStats() {
		return sortDesc ? user.betting_stats.data.sortByDesc(sortCol) : user.betting_stats.data.sortBy(sortCol);
	}

	function calcTotalPercentage() {
		return (stats.sum('client_result') / stats.sum('total_turnover')) * 100;
	}

	function calcTotalAverageBet() {
		return parseFloat(stats.sum('total_turnover') / stats.sum('total_bet_count') / 100);
	}

	//Function to calculate User Betting Stats based on Category (Racing | Sport | Mixed)
	function calcBettingStatsByCategory() {
		racingClientResult = 0;
		sportsClientResult = 0;
		multiClientResult = 0;
		racingTurnover = 0;
		sportsTurnover = 0;
		multiTurnover = 0;
		racingCount = 0;
		sportsCount = 0;
		multiCount = 0;
		racingTotalPercentage = 0;
		sportsTotalPercentage = 0;
		multiTotalPercentage = 0;

		user.betting_stats.data.each((stat) => {
			if (stat.bet_category.name === 'Racing') {
				racingCount = racingCount + stat.bet_count;
				racingClientResult = racingClientResult + stat.client_result;
				racingTurnover = racingTurnover + stat.turnover;
			} else if (stat.bet_category.name === 'Sport') {
				sportsCount = sportsCount + stat.bet_count;
				sportsClientResult = sportsClientResult + stat.client_result;
				sportsTurnover = sportsTurnover + stat.turnover;
			} else if (stat.bet_category.name === 'Mixed') {
				multiCount = multiCount + stat.bet_count;
				multiClientResult = multiClientResult + stat.client_result;
				multiTurnover = multiTurnover + stat.turnover;
			}
		});

		racingTotalPercentage =
			racingClientResult == 0 || racingTurnover == 0 ? 0 : (racingClientResult / racingTurnover) * 100;
		sportsTotalPercentage =
			sportsClientResult == 0 || sportsTurnover == 0 ? 0 : (sportsClientResult / sportsTurnover) * 100;
		multiTotalPercentage = multiClientResult == 0 || multiTurnover == 0 ? 0 : (multiClientResult / multiTurnover) * 100;
	}
</script>

{#if $ACLStore && ACLStore.granted('users.read.betting_stats', true)}
	<StatsReport bind:open={showStatsReport} {user} />
{/if}

{#if user}
	<div class="flex flex-col md:w-1/2 text-dark-grey bg-gray-50 min-h-76" style="max-height: calc(43vh)">
		<div class="flex items-center px-2 bg-dark-grey text-light">
			<h2 class="flex-1 py-2 text-xl italic font-sans-condensed whitespace-nowrap">Betting Stats</h2>
			<p class="px-2 text-xs">*stats are updated after every bet is resulted</p>
			{#if $ACLStore && ACLStore.granted('users.read.betting_stats', true)}
				<button
					class="py-1 ml-2 btn btn-primary focus:outline-none whitespace-nowrap"
					on:click={() => (showStatsReport = true)}
				>
					<span class="flex flex-row items-center justify-center">
						Report
						<Icon name="calendar" classes="w-4 h-4 ml-2 mr-0.5" />
					</span>
				</button>
			{/if}
		</div>
		<div class=" overflow-auto">
			{#if stats && stats.count() > 0}
				<div class="table w-full">
					<div class="table-header-group">
						<div class="table-row text-sm text-center bg-grey text-light">
							{#each Object.entries(statCols) as col, i}
								<div
									class:text-left={i < 1}
									class:pl-2={i < 1}
									class:text-primary={sortCol === col[1]}
									class="sticky top-0 table-cell py-2 pl-1 cursor-pointer bg-grey"
									on:click={() => {
										if (sortCol === col[1]) sortDesc = !sortDesc;
										sortCol = col[1];
									}}
								>
									<div class="flex justify-center">
										{col[0]}
										{#if col[1] === sortCol}
											<Icon name={sortDesc ? 'chevron-down' : 'chevron-up'} classes="h-2 self-center -ml-1" />
										{/if}
									</div>
								</div>
							{/each}
						</div>
					</div>
					<div class="table-row-group">
						{#each stats.all() as stat}
							<div class="table-row text-sm text-center hover:bg-gray-200">
								<div class="table-cell px-2 py-2 text-left border-b">
									{#if stat.classification && parseInt(stat.classification.id) !== 999}
										{#if stat.race_type}
											{stat.race_type.name} - {stat.bet_type.name}
										{:else if stat.sport}
											{stat.sport.name}
										{:else}
											{stat.bet_type.name} - {stat.bet_category.name}
										{/if}
									{:else if stat.race_type}
										{stat.race_type.name}
									{:else if stat.sport}
										{stat.sport.name}
									{:else}
										{stat.bet_category.name}
									{/if}
								</div>
								<div class="table-cell px-4 py-2 border-b">
									{stat.classification ? stat.classification.name : '-'}
								</div>
								<div class="table-cell px-4 py-2 border-b">
									{CurrencyFormat(stat.client_result)}
								</div>
								<div class="table-cell px-4 py-2 border-b">
									{CurrencyFormat(stat.turnover)}
								</div>
								<div class="table-cell px-4 py-2 border-b">
									{CurrencyFormat(stat.bonus_turnover)}
								</div>
								<div class="table-cell px-4 py-2 border-b">
									{NumberFormat(stat.percentage, true)}%
								</div>
								<div class="table-cell px-4 py-2 border-b">
									{stat.bet_count}
								</div>
								<div class="table-cell px-4 py-2 border-b">
									{stat.bonus_bet_count}
								</div>
								<div class="table-cell px-4 py-2 border-b">
									{CurrencyFormat(stat.bet_average)}
								</div>
							</div>
						{/each}
						<div class="table-row text-sm text-center">
							<div
								class="whitespace-nowrap sticky bottom-0 table-cell px-2 py-2 space-y-2 font-semibold text-left bg-white border-t border-grey"
							>
								<div>Total Lifetime</div>
								<div>Total Daily</div>
								<div>Racing Lifetime</div>
								<div>Sport Lifetime</div>
								<div>Mixed Lifetime</div>
							</div>
							<div class="sticky bottom-0 table-cell px-2 py-2 bg-white border-t border-grey" />
							<div
								class=" whitespace-nowrap sticky bottom-0 table-cell px-2 py-2 space-y-2 font-bold bg-white border-t border-grey"
							>
								<div>{CurrencyFormat(stats.sum('client_result'))}</div>
								<div>{CurrencyFormat(daily.client_result)}</div>
								<div>{CurrencyFormat(racingClientResult)}</div>
								<div>{CurrencyFormat(sportsClientResult)}</div>
								<div>{CurrencyFormat(multiClientResult)}</div>
							</div>
							<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
								<div>{CurrencyFormat(stats.sum('turnover'))}</div>
								<div>{CurrencyFormat(daily.turnover)}</div>
								<div>{CurrencyFormat(racingTurnover)}</div>
								<div>{CurrencyFormat(sportsTurnover)}</div>
								<div>{CurrencyFormat(multiTurnover)}</div>
							</div>
							<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
								<div>{CurrencyFormat(stats.sum('bonus_turnover'))}</div>
								<div>{CurrencyFormat(daily.bonus_turnover)}</div>
							</div>
							<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
								<div>{NumberFormat(calcTotalPercentage(), true)}%</div>
								<div>{NumberFormat(daily.percentage, true)}%</div>
								<div>({NumberFormat(racingTotalPercentage, true)}%)</div>
								<div>({NumberFormat(sportsTotalPercentage, true)}%)</div>
								<div>({NumberFormat(multiTotalPercentage, true)}%)</div>
							</div>
							<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
								<div>{stats.sum('bet_count')}</div>
								<div>{daily.bet_count}</div>
								<div>{racingCount}</div>
								<div>{sportsCount}</div>
								<div>{multiCount}</div>
							</div>
							<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
								<div>{stats.sum('bonus_bet_count')}</div>
								<div>{daily.bonus_bet_count}</div>
							</div>
							<div class="sticky bottom-0 table-cell px-4 py-2 space-y-2 bg-white border-t border-grey">
								<div>{CurrencyFormat(calcTotalAverageBet(), true)}</div>
								<div>{CurrencyFormat(daily.bet_average)}</div>
							</div>
						</div>
					</div>
				</div>
			{:else}
				<div class="w-full py-10 text-center">No betting stats available</div>
			{/if}
		</div>
	</div>
{/if}
